import { signal, computed } from '@preact/signals';
import i18next from 'i18next';
import { api } from '../api/client.js';

interface User {
  id: string;
  email: string;
  role: 'CUSTOMER' | 'ADMIN';
  name: string | null;
  language: string | null;
  has_active_plan?: boolean;
  is_super_admin?: boolean;
  tenant_id?: string;
  tenant_name?: string | null;
  tenant_slug?: string | null;
}

// Cache de auth no localStorage para renderização instantânea (sem flash)
const AUTH_CACHE_KEY = 'wlc_auth_cache';

function loadCachedUser(): User | null {
  try {
    const cached = localStorage.getItem(AUTH_CACHE_KEY);
    if (!cached) return null;
    const parsed = JSON.parse(cached);
    // Cache válido por 24h
    if (parsed.ts && Date.now() - parsed.ts < 86400000) return parsed.user;
    localStorage.removeItem(AUTH_CACHE_KEY);
  } catch { /* ignore */ }
  return null;
}

function saveCachedUser(u: User | null) {
  try {
    if (u) {
      localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify({ user: u, ts: Date.now() }));
    } else {
      localStorage.removeItem(AUTH_CACHE_KEY);
    }
  } catch { /* ignore */ }
}

const cachedUser = loadCachedUser();
export const user = signal<User | null>(cachedUser);
export const authLoading = signal(!cachedUser);
/** Whether the initial auth check has completed at least once */
export const authChecked = signal(!!cachedUser);
export const isAdmin = computed(() => user.value?.role === 'ADMIN');
export const isLoggedIn = computed(() => user.value !== null);
export const hasActivePlan = computed(() => user.value?.has_active_plan === true);
export const isSuperAdmin = computed(() => user.value?.is_super_admin === true);
/** Tenant ID do usuário logado (para isolamento de dados) */
export const userTenantId = computed(() => user.value?.tenant_id || null);
export const userTenantName = computed(() => user.value?.tenant_name || null);

export async function fetchMe(): Promise<void> {
  authLoading.value = true;
  try {
    const data = await api.getQuiet<{ user: User }>('/auth/me');
    user.value = data.user;
    saveCachedUser(data.user);
    // Aplicar idioma do perfil do usuário (se configurado e não tem locale na URL)
    if (data.user?.language) {
      const urlHasLocale = /^\/(pt-BR|en-US|es-ES|fr-FR|de-DE)(\/|$)/.test(window.location.pathname);
      if (!urlHasLocale && i18next.language !== data.user.language) {
        i18next.changeLanguage(data.user.language);
      }
    }
  } catch {
    user.value = null;
    saveCachedUser(null);
  } finally {
    authLoading.value = false;
    authChecked.value = true;
  }
}

export async function logout(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } finally {
    user.value = null;
    saveCachedUser(null);
    window.location.href = '/chat';
  }
}
